import React from 'react';
import { 
    FlexDiv, 
    TextDefault,
    Heading4,
    TextInput,
    Button
} from '@awarego-ui/components';
import { Checkbox, Radio } from '@mui/material';

function BlockRenderer({ block, isPreview = false, onUpdate }) {
    if (!block) return null;

    const renderPreview = () => {
        switch (block.type) {
            case 'radio':
                return (
                    <FlexDiv column gap={12}>
                        <Heading4>{block.title}</Heading4>
                        {block.options?.map((option, index) => (
                            <FlexDiv key={index} alignCenter gap={8}>
                                <Radio disabled />
                                <TextDefault>{option}</TextDefault>
                            </FlexDiv>
                        ))}
                    </FlexDiv>
                );

            case 'checkbox':
                return (
                    <FlexDiv column gap={12}>
                        <Heading4>{block.title}</Heading4>
                        {block.options?.map((option, index) => (
                            <FlexDiv key={index} alignCenter gap={8}>
                                <Checkbox disabled />
                                <TextDefault>{option}</TextDefault>
                            </FlexDiv>
                        ))}
                    </FlexDiv>
                );

            case 'numericalscale':
                return (
                    <FlexDiv column gap={12}>
                        <Heading4>{block.title}</Heading4>
                        <FlexDiv alignCenter gap={8}>
                            <TextDefault>{block.min || 1}</TextDefault>
                            <TextDefault>—</TextDefault>
                            <TextDefault>{block.max || 10}</TextDefault>
                        </FlexDiv>
                    </FlexDiv>
                );

            case 'yes-no-list':
                return (
                    <FlexDiv column gap={12}>
                        <Heading4>{block.title}</Heading4>
                        {block.questions?.map((q, index) => (
                            <FlexDiv key={index} alignCenter gap={8}>
                                <TextDefault>{q.text}</TextDefault>
                                <FlexDiv gap={8}>
                                    <Button size="small" disabled>Yes</Button>
                                    <Button size="small" disabled>No</Button>
                                </FlexDiv>
                            </FlexDiv>
                        ))}
                    </FlexDiv>
                );

            case 'orderablelist':
                return (
                    <FlexDiv column gap={12}>
                        <Heading4>{block.title}</Heading4>
                        <TextDefault>
                            {block.items?.length || 0} items to order
                        </TextDefault>
                    </FlexDiv>
                );

            case 'conversation':
                return (
                    <FlexDiv column gap={12}>
                        <Heading4>{block.title}</Heading4>
                        <TextDefault>
                            {block.messages?.length || 0} messages
                        </TextDefault>
                    </FlexDiv>
                );

            case 'password-tester':
                return (
                    <FlexDiv column gap={12}>
                        <Heading4>{block.title}</Heading4>
                        <TextInput type="password" placeholder="Enter password" disabled />
                    </FlexDiv>
                );

            case 'decision':
                return (
                    <FlexDiv column gap={12}>
                        <Heading4>{block.title}</Heading4>
                        <TextDefault>Decision tree with video</TextDefault>
                    </FlexDiv>
                );

            case 'questionwithbuttons':
                return (
                    <FlexDiv column gap={12}>
                        <Heading4>{block.title}</Heading4>
                        <FlexDiv gap={8}>
                            {block.buttons?.map((button, index) => (
                                <Button key={index} size="small" disabled>
                                    {button}
                                </Button>
                            ))}
                        </FlexDiv>
                    </FlexDiv>
                );

            case 'questionwithimage':
                return (
                    <FlexDiv column gap={12}>
                        <Heading4>{block.title}</Heading4>
                        {block.imageUrl ? (
                            <img 
                                src={block.imageUrl} 
                                alt="Question image" 
                                style={{ maxWidth: 200, maxHeight: 150 }}
                            />
                        ) : (
                            <TextDefault lighter>No image selected</TextDefault>
                        )}
                    </FlexDiv>
                );

            case 'questionwithsocialpost':
                return (
                    <FlexDiv column gap={12}>
                        <Heading4>{block.title}</Heading4>
                        <TextDefault>Social post preview</TextDefault>
                    </FlexDiv>
                );

            case 'groupOfBlocks':
                return (
                    <FlexDiv column gap={12}>
                        <Heading4>{block.title}</Heading4>
                        <TextDefault>
                            {block.blocks?.length || 0} nested blocks
                        </TextDefault>
                    </FlexDiv>
                );

            default:
                return (
                    <FlexDiv column gap={12}>
                        <Heading4>{block.title || 'Unknown Block'}</Heading4>
                        <TextDefault>Type: {block.type}</TextDefault>
                    </FlexDiv>
                );
        }
    };

    const renderEditor = () => {
        return (
            <FlexDiv column gap={12}>
                <TextInput
                    value={block.title || ''}
                    onChange={(e) => onUpdate?.({ title: e.target.value })}
                    placeholder="Block title"
                />
                
                {block.type === 'radio' || block.type === 'checkbox' ? (
                    <FlexDiv column gap={8}>
                        <TextDefault bold>Options:</TextDefault>
                        {block.options?.map((option, index) => (
                            <FlexDiv key={index} alignCenter gap={8}>
                                <TextInput
                                    value={option}
                                    onChange={(e) => {
                                        const newOptions = [...block.options];
                                        newOptions[index] = e.target.value;
                                        onUpdate?.({ options: newOptions });
                                    }}
                                    placeholder="Option text"
                                />
                                <Button
                                    size="small"
                                    mode="danger"
                                    onClick={() => {
                                        const newOptions = block.options.filter((_, i) => i !== index);
                                        onUpdate?.({ options: newOptions });
                                    }}
                                >
                                    ×
                                </Button>
                            </FlexDiv>
                        ))}
                        <Button
                            size="small"
                            onClick={() => {
                                const newOptions = [...(block.options || []), 'New Option'];
                                onUpdate?.({ options: newOptions });
                            }}
                        >
                            Add Option
                        </Button>
                    </FlexDiv>
                ) : null}

                {block.type === 'numericalscale' ? (
                    <FlexDiv alignCenter gap={12}>
                        <TextDefault>Min:</TextDefault>
                        <TextInput
                            type="number"
                            value={block.min || 1}
                            onChange={(e) => onUpdate?.({ min: parseInt(e.target.value) })}
                            style={{ width: 80 }}
                        />
                        <TextDefault>Max:</TextDefault>
                        <TextInput
                            type="number"
                            value={block.max || 10}
                            onChange={(e) => onUpdate?.({ max: parseInt(e.target.value) })}
                            style={{ width: 80 }}
                        />
                    </FlexDiv>
                ) : null}

                <FlexDiv alignCenter gap={8}>
                    <Checkbox
                        checked={block.required || false}
                        onChange={(e) => onUpdate?.({ required: e.target.checked })}
                    />
                    <TextDefault>Required</TextDefault>
                </FlexDiv>
            </FlexDiv>
        );
    };

    return (
        <div className="block-renderer">
            {isPreview ? renderPreview() : renderEditor()}
        </div>
    );
}

export default BlockRenderer;
